import {
  Box,
  HStack,
  Text,
  Divider,
  Center,
  Image,
  Switch,
  // useDisclosure,
  useToast,
  SlideFade,
  Flex,
} from '@chakra-ui/react';
import AppRoutes from 'config/AppRoutes';
import PageLayout from 'ui/layout/PageLayout';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AnswersSelector,
  NumPanelFromMapSelector,
  NumPanelExplicitSelector,
  FlowSelector,
  BusinessChoiceSelector,
} from 'store/selectors';
import Button from 'ui/molecules/Button';
import RoundSlider from 'ui/molecules/RoundSlider';
import {
  updateBattery,
  updateBill,
  updateConsumption,
  updateCounter,
  updateInverter,
  updateNumPanelExplicit,
  updatePanel,
  updateRoofType,
  updateVehicle,
} from 'store/answers/answersSlice';
import { PromoCodeResult } from 'api';
import RoundedCard from 'ui/molecules/RoundedCard';
import RoundedBox from 'ui/molecules/RoundedBox';
import SummaryListItem from 'ui/molecules/SummaryListItem';

// assets
import estimateImage from 'assets/images/summary_estimate.png';
import gainImage from 'assets/images/summary_gain.png';
import co2Image from 'assets/images/summary_co2.svg';
import treeImage from 'assets/images/summary_tree.svg';
import vehicleYesImage from 'assets/images/summary_vehicle_yes.svg';
import vehicleNoImage from 'assets/images/summary_vehicle_no.svg';
import batteryYesImage from 'assets/images/summary_battery_yes.svg';
import batteryNoImage from 'assets/images/summary_battery_no.svg';
import {
  SummaryChoiceIcon,
  SummaryLocationIcon,
  SummeryRoofIcon,
} from 'ui/icons/Icons';
import SummaryForm from 'components/SummaryForm/SummaryForm';
import SummaryHouseImage from 'components/SummaryHouseImage';
import useGetPVCalc from 'api/useGetPVCalc';
import {
  calculateConsumptionBaseOnBill,
  getCalculatedYearlyGain,
  getEstimatedPanelsForConsumption,
  GetHouseOrientation,
  getPanelPower,
  GetPeakPower,
  getPromoPrice,
  GetTotalC02Saved,
  GetTotalTreeSaved,
} from 'utils/calculation/calculation';
import useGetRegionPrice from 'api/useGetRegionPrice';
import { getPanelByID, getPanelName } from 'utils/panelUtils';
import { useMemo, useRef, useState } from 'react';
import { getOfferItems } from 'utils/getOfferItems';
import config from 'config/config';
import Map from 'components/Map';
import SelectBox from 'ui/molecules/SelectBox';
import getLightAmountValue from 'utils/getLightAmountValue';
import tagging from 'features/tagging/tagging';
import SummaryPriceBox from 'components/SummaryPriceBox';
import { useIsVisibleOnScreen } from 'utils/hooks/useIsVisibleOnScreen';
import useIsMobileScreen from 'utils/hooks/useIsMobileScreen';
import { numberToString } from 'utils/stringUtils';
import { FlowChoiceType } from 'features/utils/types';
import PanelIndicator from 'components/PanelIndicator';
import { AnswersState } from 'schemas/answerStateSchema';
import useTVAText from 'utils/hooks/useTVAText';
import getFirstExistingTranslation from 'utils/getFirstExistingTranslation';
import { getTotalInstallationPrice } from 'utils/calculation/getTotalInstallationPrice';
import usePanelList from './PagePanel/hooks/usePanelList';
import useConsumptionConfig from './PageConsumption/hooks/useConsumptionConfig';
import useBillConfig from './PageBill/hooks/useBillConfig';

type Props = {
  preview?: boolean;
  savedPromoCodeDetails?: PromoCodeResult;
};

const PageSummary = ({ preview = false, savedPromoCodeDetails }: Props) => {
  const currentRoute = AppRoutes.SUMMARY;
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobileScreen();
  const dispatch = useDispatch();
  const flowChoice: FlowChoiceType = useSelector(FlowSelector);
  const businessChoice = useSelector(BusinessChoiceSelector);
  const priceBoxRefAnchor = useRef<HTMLDivElement>(null);
  const isPriceBoxVisible = useIsVisibleOnScreen(priceBoxRefAnchor, '-100px');
  const formBoxRefAnchor = useRef<HTMLDivElement>(null);
  const isFormBoxVisible = useIsVisibleOnScreen(formBoxRefAnchor, '50px');
  const formTitleRefAnchor = useRef<HTMLDivElement>(null);
  const isFormTitleVisible = useIsVisibleOnScreen(formTitleRefAnchor, '50px');
  const endButtonRefAnchor = useRef<HTMLDivElement>(null);
  const isEndButtonVisible = useIsVisibleOnScreen(endButtonRefAnchor, '0px');
  const [promoCodeDetails, setPromoCodeDetails] = useState<
    PromoCodeResult | undefined
  >(savedPromoCodeDetails);
  const [numPanelSlider, setNumPanelSlider] = useState<number | undefined>(
    undefined
  );
  const toast = useToast();
  const tvaTxt = useTVAText(businessChoice);
  // const {
  //   isOpen: emailFormIsOpen,
  //   onOpen: showEmailForm,
  //   onClose: closeEmailForm,
  // } = useDisclosure();

  // case preview mode (estimate), we check if we have the "noForm" param
  const noForm =
    preview && !(window && window.location.href.includes('noForm=0'));

  const answers = useSelector(AnswersSelector) as AnswersState;
  const {
    address,
    addressDetails,
    panelId,
    roofAngle,
    roofType,
    mapDetail,
    counter,
    inverter,
    consumption: explicitConsumption,
    billAmount: explicitBillAmount,
    needBattery,
    needVehicle,
    extraServices,
  } = answers;
  const panelList = usePanelList();
  const consumptionConfig = useConsumptionConfig();
  const billConfig = useBillConfig();

  const numPanelsFromMap = useSelector(NumPanelFromMapSelector);
  const numPanelsExplicit = useSelector(NumPanelExplicitSelector);

  const regionPrice = useGetRegionPrice(addressDetails?.postCode);

  // Consumption can be given by user or calculated based on bill amount
  const consumption = useMemo(() => {
    if (explicitConsumption !== undefined) return explicitConsumption;
    if (explicitBillAmount && regionPrice)
      return calculateConsumptionBaseOnBill(explicitBillAmount, regionPrice);
    return 0;
  }, [explicitConsumption, explicitBillAmount, regionPrice]);

  const numPanels =
    numPanelsExplicit ||
    numPanelsFromMap ||
    (panelId &&
      getEstimatedPanelsForConsumption(panelId, panelList, consumption)) ||
    0;

  // Bill amount can be given by user or calculated based on consumption
  const billAmount = useMemo(() => {
    if (explicitBillAmount !== undefined) return explicitBillAmount;
    if (explicitConsumption && regionPrice)
      return (explicitConsumption / 12) * regionPrice.price_cons;
    return 0;
  }, [explicitConsumption, explicitBillAmount, regionPrice]);

  const houseOrientation = useMemo(
    () => GetHouseOrientation(mapDetail),
    [mapDetail]
  );
  const peakPower = useMemo(() => {
    if (panelId) {
      return GetPeakPower(numPanels, getPanelPower(panelId, panelList));
    }
    return 0;
  }, [numPanels, panelId, panelList]);

  const { yearlyProduction: estimatedYearlyProduction } = useGetPVCalc({
    lat: addressDetails?.geoCode.lat || -1,
    lng: addressDetails?.geoCode.lng || -1,
    peakPower,
    roofAngle: roofAngle!,
    orientation: houseOrientation,
  });
  const batteryI18Key = `summary.choices.battery.${needBattery ? 'yes' : 'no'}${
    !needBattery && preview ? '.finished' : ''
  }`;

  const batteryImage = needBattery ? batteryYesImage : batteryNoImage;

  const vehicleI18Key = `summary.choices.vehicle.${needVehicle ? 'yes' : 'no'}${
    !needVehicle && preview ? '.finished' : ''
  }`;

  const vehicleImage = needVehicle ? vehicleYesImage : vehicleNoImage;

  const gainYear = useMemo(() => {
    if (estimatedYearlyProduction && regionPrice)
      return getCalculatedYearlyGain(
        consumption,
        estimatedYearlyProduction,
        peakPower,
        !!needBattery,
        regionPrice
      );
    return 0;
  }, [
    estimatedYearlyProduction,
    needBattery,
    regionPrice,
    consumption,
    peakPower,
  ]);

  const estimatedYearlyProductionText = estimatedYearlyProduction
    ? numberToString(estimatedYearlyProduction)
    : undefined;

  const gainTotal = gainYear * 25;
  const gainMonth = gainYear / 12;

  const totalCost = panelId ? getTotalInstallationPrice(numPanels, answers) : 0;
  const costWithPromo = promoCodeDetails
    ? getPromoPrice(totalCost, promoCodeDetails, peakPower)
    : totalCost;
  const amortYearAmount = Math.round(totalCost / gainYear);
  const co2Amount = GetTotalC02Saved(estimatedYearlyProduction || 0) / 1000;

  const treeAmount = GetTotalTreeSaved(estimatedYearlyProduction || 0);

  // we want to be sure the offer items are "recalculated" each time we change the answers or num panels
  const offerItems = getOfferItems(answers, numPanels);

  // b2b users can ask for battery or charger information
  const isB2B = businessChoice === 'b2b';
  const wantBatteryInformation =
    isB2B && extraServices?.some((s) => s.id === 'battery');
  const wantCarChargerInformation =
    isB2B && extraServices?.some((s) => s.id === 'charger');

  // --------------------- handlers ------------------------

  const gtmUpdate = (modifiedItem: string) => {
    tagging.tagSummaryEdit(modifiedItem);
    tagging.tagUpdateEvent(currentRoute);
  };

  const handleRestart = () => {
    const basePath = window.location.href.split('#')[0];
    window.location.href = basePath;
  };

  const handleLeadFormSuccess = (configID: string) => {
    toast({
      title: t('form.submit.success.toast'),
      status: 'success',
      position: 'top',
      duration: 5000,
      isClosable: true,
    });
    navigate(`${AppRoutes.THANKS}?configID=${configID}`);
  };

  // const handleEmailFormSuccess = () => {
  //   setEmailFormSubmitted(true);
  // };

  // --------------------- render ------------------------

  return (
    <PageLayout
      title={t('summary.title')}
      // do not use sticky button on this page
      // buttonLabel={t('common.modify')}
      // buttonCallback={handleContinue}
    >
      <Center width="80%">
        <Text textAlign="center">{t('summary.desc')}</Text>
      </Center>

      {/* ----------- PIC -------------- */}
      {panelId && (
        <Box position="relative" zIndex={-1}>
          <Box
            transform={
              isB2B
                ? {
                    base: 'scale(1.1) translateY(-5px)',
                    md: 'scale(1.5) translateY(10px)',
                  }
                : ''
            }
          >
            <SummaryHouseImage
              panelAssetIndex={
                getPanelByID(panelId, panelList)?.assetIndex || -1
              }
              vehicle={needVehicle || false}
              battery={needBattery || false}
              roofType={roofType || 'tuile'}
              roofAngle={roofAngle || 0}
              lightAmount={getLightAmountValue(
                consumption,
                consumptionConfig.min,
                consumptionConfig.maxForLightAnimation
              )}
            />
          </Box>
          <PanelIndicator
            variant="small"
            amount={numPanels}
            txt={t('common.panels.needed')}
            top={isB2B ? { base: '60px', md: '80px' } : { base: '0', md: '0' }}
            right={{ base: '0px', md: '-20px' }}
            textPosition="left"
          />
        </Box>
      )}

      {/* ----------- ADRESS -------------- */}
      {flowChoice !== 'simple' && (
        <RoundedBox color="white" bgColor="secondaryColor" h={14}>
          <HStack h="full" justifyItems="center" px={1}>
            <SummaryLocationIcon
              insideColor="black"
              outsideColor="white"
              boxSize={7}
            />
            <Text fontSize="md" fontWeight="bold">
              {address}
            </Text>
          </HStack>
        </RoundedBox>
      )}

      {/* ----------- ROOF -------------- */}
      {mapDetail && flowChoice !== 'simple' && (
        <RoundedCard color="primaryColor">
          <HStack paddingBottom="2">
            <SummeryRoofIcon />
            <Text fontSize="xl" fontWeight="bold" paddingLeft="2" color="black">
              {t('summary.roof.title')}
            </Text>
          </HStack>
          <Box
            backgroundColor="reno.grey100"
            width="full"
            height="300px"
            borderRadius="10px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            position="relative"
          >
            <Box overflow="hidden" height="300px" w="full">
              <Map height="300px" previewOnly />
            </Box>

            {numPanelsExplicit && numPanelsExplicit !== numPanelsFromMap && (
              <Text
                w="full"
                h="full"
                display="flex"
                justifyContent="center"
                userSelect="none"
                alignItems="center"
                bgColor="#ffffffBF"
                // opacity="0.5"
                // fontWeight="bold"
                fontSize="xl"
                color="black"
                p="5"
                textAlign="center"
                position="absolute"
              >
                {t('summary.roof.explicitPanels')}
              </Text>
            )}
          </Box>
        </RoundedCard>
      )}

      {/* ----------- CHOICES -------------- */}
      <RoundedCard>
        <HStack paddingBottom="2">
          <Box color="primaryColor">
            <SummaryChoiceIcon />
          </Box>
          <Text fontSize="xl" fontWeight="bold" paddingLeft="2">
            {t('summary.choices.title')}
          </Text>
        </HStack>
        <Divider marginLeft="-5px" marginBottom="2" />

        <SummaryListItem
          collapsedContent={
            <Box display="flex" flexDirection="row" gap="5">
              {1}
              <RoundSlider
                min={1}
                max={150}
                baseValue={numPanels}
                step={1}
                onChange={(val) => {
                  setNumPanelSlider(val);
                }} // do not dispatch on change to avoid too much calls on api...
                onChangeEnd={(val) => {
                  dispatch(updateNumPanelExplicit(val));
                  setNumPanelSlider(undefined);
                  gtmUpdate('panel_amount');
                }}
              />
              {150}
            </Box>
          }
          divider
          allowEdition={!preview}
        >
          <Box>
            {numPanels >= 25
              ? t('summary.choices.panels.num.asterix')
              : t('summary.choices.panels.num.noAsterix')}{' '}
            :{' '}
            <b> {numPanelSlider !== undefined ? numPanelSlider : numPanels}</b>
          </Box>
        </SummaryListItem>

        <SummaryListItem
          collapsedContent={
            <Box display="flex" flexDirection="row" gap="5">
              <SelectBox
                items={panelList.map((panel) => ({
                  key: panel.id,
                  label: getFirstExistingTranslation(t, i18n, [
                    `PANEL_SELECTION_panel_${panel.assetIndex}_${businessChoice}.title`,
                    `PANEL_SELECTION_panel_${panel.assetIndex}.title`,
                  ]),
                }))}
                selectedKey={panelId || ''}
                onChange={(val) => {
                  dispatch(updatePanel(val));
                  gtmUpdate('panel_type');
                }}
              />
            </Box>
          }
          divider
          allowEdition={!preview}
        >
          <Box>
            {t('summary.choices.panels.type')} :{' '}
            <b> {getPanelName(panelId!, panelList, businessChoice)}</b>
          </Box>
        </SummaryListItem>

        <SummaryListItem
          collapsedContent={
            <Box display="flex" flexDirection="row" gap="5">
              <SelectBox
                items={config.options.roofTypes
                  .filter(
                    (x) => x.filter === (roofAngle! > 0 ? 'normal' : 'flat')
                  )
                  .map(({ id: roofTypeId }) => ({
                    key: roofTypeId,
                    label: t(`ROOF_TYPE.${roofTypeId}`),
                  }))}
                selectedKey={roofType || ''}
                onChange={(val) => {
                  dispatch(updateRoofType(val));
                  gtmUpdate('roof_type');
                }}
              />
            </Box>
          }
          divider
          allowEdition={!preview}
        >
          <Box>
            {t('summary.choices.roof.type')} :{' '}
            <b> {t(`ROOF_TYPE.${roofType}`)}</b>
          </Box>
        </SummaryListItem>

        <SummaryListItem divider>
          <Box>
            {t('summary.choices.angle')} : <b> {roofAngle}°</b>
          </Box>
        </SummaryListItem>

        {/* COUNTER */}
        {flowChoice === 'complex' && (
          <SummaryListItem
            collapsedContent={
              <Box display="flex" flexDirection="row" gap="5">
                <SelectBox
                  items={config.options.counter.map(
                    ({ id: counterOptionId }) => ({
                      key: counterOptionId,
                      label: t(`COUNTER.${counterOptionId}`),
                    })
                  )}
                  selectedKey={counter || ''}
                  onChange={(val) => {
                    dispatch(updateCounter(val));
                    gtmUpdate('counter_type');
                  }}
                />
              </Box>
            }
            divider
            allowEdition={!preview}
          >
            <Box>
              {t('summary.choices.counter')} : <b> {t(`COUNTER.${counter}`)}</b>
            </Box>
          </SummaryListItem>
        )}

        {/* INVERTER */}
        <SummaryListItem
          display="none" // TODO: reset this
          collapsedContent={
            <Box display="flex" flexDirection="row" gap="5">
              <SelectBox
                items={config.options.inverters.map(({ id: inverterId }) => ({
                  key: inverterId,
                  label: t(`INVERTER.${inverterId}.title`),
                }))}
                selectedKey={inverter || ''}
                onChange={(val) => {
                  if (val) {
                    dispatch(updateInverter(val));
                    gtmUpdate('inverter_type');
                  }
                }}
              />
            </Box>
          }
          divider
          allowEdition={!preview}
        >
          <Box>
            {t('summary.choices.inverter')} :{' '}
            <b> {t(`INVERTER.${inverter}.title`)}</b>
          </Box>
        </SummaryListItem>

        <SummaryListItem
          collapsedContent={
            <Box display="flex" flexDirection="row" gap="5">
              {billConfig.min}€
              <RoundSlider
                min={billConfig.min}
                max={billConfig.max}
                baseValue={Math.round(billAmount)}
                step={5}
                onChange={(val) => dispatch(updateBill(val))}
                onChangeEnd={(val) => {
                  dispatch(updateBill(val));
                  gtmUpdate('bill_amount');
                }}
              />
              {billConfig.max}€
            </Box>
          }
          divider
          allowEdition={!preview}
        >
          <Flex flexWrap="wrap" gap="5px">
            <Box>{t('summary.choices.bill.amount')} : </Box>
            <Box>
              <b> {numberToString(billAmount)} €</b>
            </Box>
            <Box>
              {!explicitBillAmount && (
                <span>
                  (<i>{t('summary.bill_consumption.estimated')}</i>)
                </span>
              )}
            </Box>
          </Flex>
        </SummaryListItem>

        <SummaryListItem
          collapsedContent={
            <Box display="flex" flexDirection="row" gap="5">
              {consumptionConfig.min}
              <RoundSlider
                min={consumptionConfig.min}
                max={consumptionConfig.max}
                baseValue={Math.round(consumption)}
                step={50}
                onChange={(val) => dispatch(updateConsumption(val))}
                onChangeEnd={(val) => {
                  dispatch(updateConsumption(val));
                  gtmUpdate('consumption');
                }}
              />
              {consumptionConfig.max}
            </Box>
          }
          divider={businessChoice !== 'b2b'}
          allowEdition={!preview}
        >
          <Flex flexWrap="wrap" gap="5px">
            <Box>{t('summary.choices.consumption')} : </Box>
            <Box>
              <b>
                {consumption.toFixed(0)} {t('CONSUMPTION_kwh')}{' '}
              </b>
            </Box>
            <Box>
              {!explicitConsumption && (
                <span>
                  (<i>{t('summary.bill_consumption.estimated')}</i>)
                </span>
              )}
            </Box>
          </Flex>
        </SummaryListItem>

        {businessChoice !== 'b2b' && (
          <SummaryListItem divider>
            <HStack padding="10px" gap="15px" w="full">
              <Image alt="battery-image" src={batteryImage} />
              <Text w="full">
                <Trans
                  i18nKey={batteryI18Key}
                  components={{
                    b: <b />,
                  }}
                />
              </Text>
              {!preview && (
                <Switch
                  isChecked={needBattery}
                  colorScheme="green"
                  variant="summaryPage"
                  onChange={() => {
                    dispatch(updateBattery(!needBattery));
                    gtmUpdate('battery');
                  }}
                />
              )}
            </HStack>
          </SummaryListItem>
        )}

        {businessChoice !== 'b2b' && (
          <SummaryListItem>
            <HStack padding="10px" gap="15px" w="full">
              <Image alt="vehicle-image" src={vehicleImage} />
              <Text w="full">
                <Trans
                  i18nKey={vehicleI18Key}
                  components={{
                    b: <b />,
                  }}
                />
              </Text>
              {!preview && (
                <Switch
                  isChecked={needVehicle}
                  colorScheme="green"
                  variant="summaryPage"
                  onChange={() => {
                    dispatch(updateVehicle(!needVehicle));
                    gtmUpdate('vehicle');
                  }}
                />
              )}
            </HStack>
          </SummaryListItem>
        )}
      </RoundedCard>

      {/* ----------- ESTIMATE -------------- */}
      <RoundedCard
        loading={!estimatedYearlyProductionText}
        padding="20px 20px 10px 0px"
        pl={{ base: '5px', md: '30px' }}
      >
        {estimatedYearlyProductionText && (
          <HStack
            justifyContent={{ base: 'start' }}
            w="full"
            gap={{ base: '5px', md: '15px' }}
          >
            <Flex w="70px" flexShrink={0} justifyContent="center">
              <Image
                h="80px"
                alt="estimate-image"
                src={estimateImage}
                fit="contain"
              />
            </Flex>

            <Box w="fit-content">
              <Text
                color="reno.gray"
                fontWeight="bold"
                fontSize="xl"
                w="fit-content"
              >
                {t('summary.estimate.title')}
              </Text>
              <Text color="reno.gray" fontSize="sm" maxW="300px">
                {t('summary.estimate.description')}
              </Text>
              <HStack marginTop="-2px" w="fit-content" alignItems="top">
                <Text
                  color="primaryColor"
                  fontSize={{ base: '5xl', md: '6xl' }}
                  fontWeight="bold"
                >
                  {estimatedYearlyProductionText}
                </Text>
                <Text
                  color="primaryColor"
                  fontSize="sm"
                  fontWeight="bold"
                  pt={8}
                >
                  {t('CONSUMPTION_kwh')}
                </Text>
              </HStack>
            </Box>
          </HStack>
        )}
      </RoundedCard>

      {/* ----------- GAIN -------------- */}
      <RoundedCard
        loading={!gainTotal}
        padding="20px 20px 10px 0px "
        pl={{ base: '5px', md: '30px' }}
      >
        <HStack
          justifyContent="start"
          pb="10px"
          gap={{ base: '5px', md: '15px' }}
        >
          <Flex w="70px" flexShrink={0} justifyContent="center">
            <Image h="80px" alt="gain-image" src={gainImage} fit="contain" />
          </Flex>

          <Box flex="0 1 auto">
            <Text color="reno.gray" fontSize="xl" fontWeight="bold">
              {t('summary.gain.title')}
            </Text>
            <HStack marginTop="-10px">
              <Text
                color="primaryColor"
                fontSize={{ base: '5xl', md: '6xl' }}
                fontWeight="bold"
              >
                {numberToString(gainTotal)}
              </Text>
              <Text
                color="primaryColor"
                fontSize="2xl"
                fontWeight="bold"
                pt={2}
              >
                {t('common.eur')}
              </Text>
            </HStack>
          </Box>
        </HStack>

        <Divider />

        <Text
          paddingTop="5"
          fontSize="xl"
          textAlign="center"
          fontWeight="bold"
          lineHeight="25px"
        >
          {t('summary.gain.bill')}
        </Text>
        <HStack display="flex" justifyContent="space-evenly">
          <HStack>
            <Text
              color="primaryColor"
              fontSize={{ base: '4xl', md: '6xl' }}
              fontWeight="bold"
            >
              {numberToString(gainMonth, 0)}
            </Text>
            <Text color="primaryColor" fontWeight="bold" pt={2}>
              {t('summary.gain.bill.month')}
            </Text>
          </HStack>

          <Divider
            orientation="vertical"
            height="50px"
            borderColor="reno.grey200"
          />
          <HStack>
            <Text
              color="primaryColor"
              fontSize={{ base: '4xl', md: '6xl' }}
              fontWeight="bold"
            >
              {numberToString(gainYear, 0)}
            </Text>
            <Text color="primaryColor" fontWeight="bold" pt={2}>
              {t('summary.gain.bill.year')}
            </Text>
          </HStack>
        </HStack>

        <Divider />
        <Center paddingTop="2">
          <Text paddingRight="6px">{t('summary.gain.bill.amort.info')}</Text>
          <Text
            fontWeight="bold"
            color="primaryColor"
            backgroundColor="primaryColor_opacity"
            borderRadius="20px"
            padding="5px 15px"
            whiteSpace="nowrap"
          >
            {t('summary.gain.bill.amort.year', { amount: amortYearAmount })}
          </Text>
        </Center>
      </RoundedCard>

      {/* ----------- ECOLOGICAL IMPACT -------------- */}
      <RoundedCard>
        <Center pt={3}>
          <Text fontWeight="bold" fontSize="xl">
            {t('summary.eco.title')}
          </Text>
        </Center>
        <HStack
          padding="20px 0px"
          display="flex"
          justifyContent="space-evenly"
          textAlign="center"
        >
          <Box width="50%">
            <Center>
              <Image alt="co2-image" src={co2Image} />
            </Center>
            <Text color="primaryColor">
              {t('summary.eco.co2.title', {
                amount: numberToString(co2Amount),
              })}
            </Text>
            <Text fontSize="sm">{t('summary.eco.co2.sub')}</Text>
          </Box>

          <Divider
            orientation="vertical"
            borderColor="reno.grey200"
            height="100px"
          />

          <Box width="50%" display="flex" flexDirection="column">
            <Center>
              <Image alt="co2-image" src={treeImage} />
            </Center>

            <Text color="primaryColor">
              {t('summary.eco.tree.title', { amount: treeAmount })}
            </Text>
            <Text fontSize="sm">{t('summary.eco.tree.sub')}</Text>
          </Box>
        </HStack>
      </RoundedCard>

      {/* ----------- TOTAL PRICE -------------- */}
      <Box ref={priceBoxRefAnchor} />
      <SummaryPriceBox
        isPreview={preview}
        price={totalCost}
        promoPrice={costWithPromo}
        promoCode={promoCodeDetails}
        numPanels={numPanels}
      />

      {/* ----------- LEAD FORM -------------- */}
      <Box ref={formBoxRefAnchor} />
      {!noForm && answers && offerItems && (
        <SummaryForm
          preview={preview}
          setPromoCodeDetails={setPromoCodeDetails}
          numPanels={numPanels}
          promoCodeDetails={promoCodeDetails}
          displayPrice={costWithPromo}
          offerDetail={{
            solarPanels: {
              id: offerItems.panelItem.id,
              count: numPanels,
            },
            inverters: {
              id: offerItems.inverterItem.id,
              count: offerItems.numInverters,
            },
            installationType: {
              id: offerItems.installationItem.id,
              count: numPanels,
            },
            batteries: offerItems.batteryItem && {
              id: offerItems.batteryItem.id,
              count: 1,
            },
            carCharger: offerItems.chargerItem && {
              id: offerItems.chargerItem.id,
              count: 1,
            },

            // TODO: do not forget to add the other items
            // En cas de client B2B, vu qu'il n'y a pas de batterie ni de chargeur, deux paramètres peuvent être passés pour indiquer le désir du client de recevoir de la documentation :
            wantBatteryInformation,
            wantCarChargerInformation,
          }}
          simulatorData={{
            roofInclination: answers.roofAngle!,
            electricityConsumptionPerYearKwh: consumption,
            electricityConsumptionPerYearEuro: billAmount * 12,
            installationExpectedPower: peakPower * 1000,
            installationExpectedProduction: estimatedYearlyProduction!,
          }}
          onSuccess={handleLeadFormSuccess}
          formTitleRef={formTitleRefAnchor}
        />
      )}

      {/* ----------- EMAIL FORM -------------- */}
      {/* {emailFormSubmitted ? (
        <SuccessMessage
          title={t('emailForm.submit.success.title')}
          description={t('emailForm.submit.success.description')}
        />
      ) : (
        <>
          <Modal
            isCentered
            onClose={closeEmailForm}
            isOpen={emailFormIsOpen}
            motionPreset="slideInBottom"
          >
            <ModalOverlay bg="whiteAlpha.800" backdropFilter="blur(2px)" />
            <ModalContent>
              <ModalCloseButton />
              <ModalBody>
                <EmailForm onSuccess={handleEmailFormSuccess} />
              </ModalBody>
            </ModalContent>
          </Modal>

          {!preview && (
            <Text fontSize="sm" mt={-1} color="black" textAlign="center">
              {t('summary.cta.simulation.policy')}
            </Text>
          )}
        </>
      )} */}

      <Box ref={endButtonRefAnchor} />
      <Button variant={preview ? 'none' : 'backButton'} onClick={handleRestart}>
        {t('summary.cta.restart')}
      </Button>

      {/* ----------- Sticky price -------------- */}
      <Box
        position="fixed"
        top="75vh"
        right={{ base: '0' }}
        left={{ md: 'calc(((100vw - 600px) / 2) + 600px - 1px)' }}
        userSelect="none"
      >
        <SlideFade
          in={
            !isPriceBoxVisible &&
            !isEndButtonVisible &&
            !isFormTitleVisible &&
            !isFormBoxVisible
          }
          offsetX={isMobile ? '150px' : '0px'}
        >
          <Button
            width="100%"
            m="0"
            display="flex"
            flexDir="column"
            height="100%"
            bgColor="secondaryColor"
            p="15px 20px 8px 20px !important"
            borderRadius={{
              base: '10px 0px 0px 10px',
              md: '2px 10px 10px 2px',
            }}
            w="fit-content"
            color="white"
            fontSize="20px"
            boxShadow="lg"
            onClick={() => {
              endButtonRefAnchor?.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }}
          >
            <Text w="100%" textAlign="left" fontWeight="normal">
              {t('summary.cost.sticky')} ({tvaTxt})
            </Text>
            <Text fontSize="35px" fontWeight="bold">
              {numberToString(costWithPromo)}€
            </Text>
          </Button>
        </SlideFade>
      </Box>
    </PageLayout>
  );
};

export default PageSummary;
